import React, { useId } from 'react'
import { usePopupDialog } from './PopupSurface'
import './popup-motion.css'
import { CheckCircle, Mail, Sparkles, X } from 'lucide-react'

export interface PopupSnapshotContent {
  title: string
  bodyText: string
  buttonText: string
  buttonSubtext?: string
  badgeText?: string
  heroImage?: string
}

export interface PopupSnapshotForm {
  formId: string
  submitLabel?: string
  successMessage?: string
}

export interface PopupSnapshotDisplay {
  delayMs?: number
  dismissCooldownMs?: number
}

export type PopupLayoutVariant = 'center_modal' | 'full_screen' | 'sidebar_right' | 'banner_bottom'

export interface PopupLayout {
  variant: PopupLayoutVariant
  maxWidth?: number
  contentVerticalAlign?: 'top' | 'center' | 'bottom'
}

export interface PopupBlock {
  id: string
  type: string
  [key: string]: unknown
}

export interface PopupTemplateSnapshot {
  name: string
  design_tokens: Record<string, unknown>
  content_blocks: PopupBlock[]
}

export interface PopupSnapshot {
  content: PopupSnapshotContent
  form: PopupSnapshotForm
  display?: PopupSnapshotDisplay
  template?: PopupTemplateSnapshot
  layout?: PopupLayout
}

export interface KibuPopupProps {
  snapshot: PopupSnapshot
  reopenTarget?: string
  isOpen: boolean
  isSuccess: boolean
  isSubmitting: boolean
  email: string
  errorMessage: string | null
  onEmailChange: (email: string) => void
  onSubmit: (event: React.FormEvent) => void
  onDismiss: () => void
}

export const KibuPopup: React.FC<KibuPopupProps> = ({
  snapshot,
  reopenTarget,
  isOpen,
  isSuccess,
  isSubmitting,
  email,
  errorMessage,
  onEmailChange,
  onSubmit,
  onDismiss,
}) => {
  const fieldId = useId()
  const { overlayRef, surfaceRef } = usePopupDialog(isOpen, true, onDismiss, reopenTarget)
  const { content, form } = snapshot
  const title = content.title || 'Join Kibu'
  const bodyText = content.bodyText || 'Sign up to hear from Kibu and unlock this offer.'
  const buttonText = content.buttonText || 'Continue'
  const buttonSubtext = content.buttonSubtext || ''
  const badgeText = content.badgeText || null
  const heroImage = content.heroImage || null
  const successMessage = form.successMessage || 'Check your inbox for the next step.'

  return (
    <div
      ref={overlayRef}
      aria-hidden={!isOpen}
      className={[
        'fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4',
        'journey-popup-overlay',
        isOpen ? 'pointer-events-auto' : 'pointer-events-none',
      ].join(' ')}
      data-open={isOpen}
      onClick={onDismiss}
    >
      <div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={[
          'relative w-full max-w-4xl overflow-hidden rounded-[28px] bg-white shadow-2xl',
          'journey-popup-surface',
        ].join(' ')}
        data-layout="center_modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onDismiss}
          className="absolute right-5 top-5 z-20 rounded-full bg-white/90 p-2 text-gray-500 shadow-sm transition-colors hover:text-gray-900"
          aria-label="Close popup"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="grid gap-0 md:grid-cols-[1.05fr_0.95fr]">
          {/* Left panel */}
          <div className="relative flex min-h-[520px] flex-col justify-between overflow-hidden bg-gradient-to-br from-[#f7f7ff] via-[#eff4ff] to-[#eef7f4] p-8 md:p-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(84,104,255,0.12),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(15,157,88,0.08),transparent_40%)]" />
            <div className="relative">
              {badgeText ? (
                <div className="mb-5 inline-flex rounded-full border border-black/10 bg-white/85 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-gray-600">
                  {badgeText}
                </div>
              ) : null}
              <h2 className="max-w-[12ch] text-[clamp(2rem,5vw,3.4rem)] font-semibold leading-[0.95] tracking-[-0.04em] text-[#111827]">
                {title}
              </h2>
              <p className="mt-5 max-w-xl text-[1.02rem] leading-7 text-gray-600">
                {bodyText}
              </p>
            </div>

            <div className="relative mt-10">
              {heroImage ? (
                <img
                  src={heroImage}
                  alt=""
                  className="h-56 w-full rounded-[24px] object-cover shadow-[0_20px_60px_rgba(15,23,42,0.12)]"
                />
              ) : (
                <div className="rounded-[24px] border border-white/70 bg-white/80 p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
                  <div className="flex items-center gap-3 text-[#4f46e5]">
                    <Sparkles className="h-5 w-5" />
                    <span className="text-sm font-medium uppercase tracking-[0.24em] text-[#4f46e5]">
                      Make it yours
                    </span>
                  </div>
                  <p className="mt-5 max-w-sm text-lg leading-8 text-gray-700">
                    A colour you love, with a personal touch. Choose your headphones and add your headband engraving in the basket.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right panel */}
          <div className="flex min-h-[520px] flex-col justify-center bg-white p-8 md:p-10">
            {!isSuccess ? (
              <>
                <div className="mb-6 flex items-center gap-3 text-gray-500">
                  <div className="rounded-2xl bg-black/[0.04] p-3">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="text-sm uppercase tracking-[0.24em] text-gray-500">
                    Email signup
                  </div>
                </div>

                <form onSubmit={onSubmit} className="space-y-4">
                  <div>
                    <label htmlFor={fieldId} className="mb-2 block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id={fieldId}
                      required
                      autoComplete="email"
                      aria-invalid={Boolean(errorMessage)}
                      aria-describedby={errorMessage ? `${fieldId}-error` : undefined}
                      type="email"
                      value={email}
                      onChange={(e) => onEmailChange(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-base text-gray-900 shadow-sm outline-none transition focus:border-[#4f46e5] focus:ring-4 focus:ring-[#4f46e5]/10"
                      disabled={isSubmitting}
                    />
                  </div>
                  {errorMessage ? (
                    <p id={`${fieldId}-error`} role="alert" className="text-sm text-red-600">{errorMessage}</p>
                  ) : null}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-[#111827] px-4 py-3 text-base font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? 'Submitting…' : buttonText}
                  </button>
                  {buttonSubtext ? (
                    <p className="text-sm leading-6 text-gray-500">{buttonSubtext}</p>
                  ) : null}
                  <p className="text-xs leading-5 text-gray-500">
                    This site is protected by reCAPTCHA.
                  </p>
                </form>
              </>
            ) : (
              <div className="space-y-5" role="status" aria-live="polite">
                <div className="inline-flex rounded-2xl bg-emerald-50 p-3 text-emerald-600">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-3xl font-semibold tracking-[-0.04em] text-[#111827]">
                    You're in.
                  </h3>
                  <p className="mt-3 text-base leading-7 text-gray-600">
                    {successMessage}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                  Sent to <strong>{email}</strong>
                </div>
                <button
                  type="button"
                  className="inline-flex rounded-2xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 transition hover:border-gray-300 hover:text-gray-900"
                  onClick={onDismiss}
                >
                  Continue shopping
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
